import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import { Button } from "../../components/ui/button";
import { showToastNotification } from "../../utility/helper";
import { RoutesLink } from "../../utility/RouteLinks";
import { SiteService } from "@/services/Site/Site";
import type { ISiteCreatePayload, SiteStatus } from "@/interface/site.interface";
import { ThrowError } from "@/api/callApi";

const INITIAL_FORM: ISiteCreatePayload = {
  siteName: "",
  siteCode: "",
  locationAddress: "",
  city: "",
  state: "",
  startDate: "",
  expectedEndDate: "",
  status: "active",
  notes: "",
};

const inputClass =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow";
const labelClass = "text-sm font-medium text-gray-700 mb-1.5 block";

const SiteForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<ISiteCreatePayload>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof ISiteCreatePayload, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      fetchSiteData(id);
    }
  }, [id]);

  const fetchSiteData = async (siteId: string) => {
    setLoading(true);
    try {
      const response = await SiteService.getSiteDetail(siteId);
      if (response?.success) {
        const site = response.data;
        setForm({
          siteName: site.siteName || "",
          siteCode: site.siteCode || "",
          locationAddress: site.locationAddress || "",
          city: site.city || "",
          state: site.state || "",
          status: site.status || "active",
          startDate: site.startDate || "",
          expectedEndDate: site.expectedEndDate || "",
          notes: site.notes || "",
        });
      } else {
        showToastNotification("danger", response?.message || "Site not found");
        navigate(RoutesLink.site);
      }
    } catch (error) {
      ThrowError(error);
      navigate(RoutesLink.site);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ISiteCreatePayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const nextErrors: Partial<Record<keyof ISiteCreatePayload, string>> = {};
    if (!form.siteName.trim()) nextErrors.siteName = "Site name is required";
    if (!form.siteCode.trim()) nextErrors.siteCode = "Site code is required";
    if (!form.locationAddress.trim()) nextErrors.locationAddress = "Location is required";
    if (!form.startDate) nextErrors.startDate = "Start date is required";
    if (form.expectedEndDate && form.startDate && form.expectedEndDate < form.startDate) {
      nextErrors.expectedEndDate = "End date cannot be before the start date";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = { ...form };
      let response;

      if (isEditMode && id) {
        response = await SiteService.updateSite(id, payload);
      } else {
        response = await SiteService.createSite(payload);
      }

      if (!response?.success) {
        showToastNotification("danger", response?.message || `Failed to ${isEditMode ? "update" : "add"} site`);
        return;
      }

      showToastNotification("success", response?.message || `Site ${isEditMode ? "updated" : "added"} successfully`);
      navigate(RoutesLink.site);
    } catch (error) {
      showToastNotification("danger", `Failed to ${isEditMode ? "update" : "add"} site`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading site data...</div>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Heading */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted"
            onClick={() => navigate(RoutesLink.site)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <MainHeading title={isEditMode ? "Edit Site" : "Add Site"} />
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Site Name */}
            <div>
              <label className={labelClass}>
                Site Name <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="e.g. Sadguru Main Site"
                value={form.siteName}
                onChange={(e) => handleChange("siteName", e.target.value)}
              />
              {errors.siteName && <p className="text-xs text-red-500 mt-1">{errors.siteName}</p>}
            </div>

            {/* Site Code */}
            <div>
              <label className={labelClass}>
                Site Code <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="e.g. SITE001"
                value={form.siteCode}
                onChange={(e) => handleChange("siteCode", e.target.value)}
              />
              {errors.siteCode && <p className="text-xs text-red-500 mt-1">{errors.siteCode}</p>}
            </div>

            {/* Location / Address */}
            <div className="sm:col-span-2 lg:col-span-3">
              <label className={labelClass}>
                Location / Address <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="e.g. Main Road, Industrial Area"
                value={form.locationAddress}
                onChange={(e) => handleChange("locationAddress", e.target.value)}
              />
              {errors.locationAddress && (
                <p className="text-xs text-red-500 mt-1">{errors.locationAddress}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label className={labelClass}>City</label>
              <input
                className={inputClass}
                placeholder="e.g. Ahmedabad"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </div>

            {/* State */}
            <div>
              <label className={labelClass}>State</label>
              <input
                className={inputClass}
                placeholder="e.g. Gujarat"
                value={form.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
            </div>

            {/* Status */}
            <div>
              <label className={labelClass}>Status</label>
              <select
                className={inputClass}
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value as SiteStatus)}
              >
                <option value="active">Active</option>
                <option value="on-hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className={labelClass}>
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                type="date"
                value={form.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
              />
              {errors.startDate && <p className="text-xs text-red-500 mt-1">{errors.startDate}</p>}
            </div>

            {/* Expected End Date */}
            <div>
              <label className={labelClass}>Expected End Date</label>
              <input
                className={inputClass}
                type="date"
                value={form.expectedEndDate}
                onChange={(e) => handleChange("expectedEndDate", e.target.value)}
              />
              {errors.expectedEndDate && (
                <p className="text-xs text-red-500 mt-1">{errors.expectedEndDate}</p>
              )}
            </div>

            {/* Notes */}
            <div className="sm:col-span-2 lg:col-span-3">
              <label className={labelClass}>Notes</label>
              <textarea
                className={`${inputClass} min-h-[90px] resize-y`}
                placeholder="Any additional details about this site..."
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(RoutesLink.site)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="gap-2" disabled={isSubmitting}>
              <Save size={16} />
              {isSubmitting ? "Saving..." : isEditMode ? "Update Site" : "Save Site"}
            </Button>
          </div>
        </form>
      </div>

  );
};

export default SiteForm;